import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import '../assets/TutorDetailModal.css';

const TutorDetailModal = ({ tutor, month, year, onClose, onGenerateInvoice, inline = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    if (tutor && month && year) {
      fetchTutorSummary();
    }
  }, [tutor, month, year]);

  const fetchTutorSummary = async () => {
    setLoading(true);
    setError('');
    
    try {
      // For pending/disputed endorsements, use the endorsement endpoint
      // For endorsed, use the invoice endpoint (which has more complete data)
      let response;
      if (tutor.endorsementStatus === 'endorsed') {
        response = await adminApi.getTutorInvoice(tutor._id, month, year);
        // getTutorInvoice responds with { success, data: invoiceData } — unwrap the nested data.
        setSummaryData(response.data.data);
      } else {
        // Pending or disputed - use endorsement endpoint
        response = await adminApi.getTutorEndorsementDetails(tutor._id, month, year);
        const data = response.data.data;
        
        // Transform to match invoice structure
        const transformedData = {
          tutor: data.tutor,
          lessons: data.lessons || [],
          adjustments: data.adjustments || [],
          recurringPayments: data.recurringPayments || [],
          profitShare: data.profitShare || null,
          lessonEarnings: data.summary?.lessonAmount || 0,
          adjustmentTotal: data.summary?.adjustmentAmount || 0,
          recurringTotal: data.summary?.recurringAmount || 0,
          finalAmount: data.summary?.totalAmount || 0,
          endorsementNotes: data.summary?.endorsementNotes || '',
          totals: {
            lessonEarnings: data.summary?.lessonAmount || 0,
            adjustmentTotal: data.summary?.adjustmentAmount || 0,
            recurringTotal: data.summary?.recurringAmount || 0,
            finalAmount: data.summary?.totalAmount || 0
          }
        };
        setSummaryData(transformedData);
      }
    } catch (err) {
      console.error('Failed to fetch tutor summary:', err);
      setError(err.response?.data?.message || 'Failed to load tutor summary');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', {
      timeZone: 'Asia/Singapore',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getMonthName = (monthNum) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNum - 1] || '';
  };

  const getStatusBadge = () => {
    switch (tutor.endorsementStatus) {
      case 'endorsed':
        return <span className="status-badge endorsed">✅ Endorsed</span>;
      case 'pending':
        return <span className="status-badge pending">⚠️ Pending</span>;
      case 'disputed':
        return <span className="status-badge disputed">❌ Disputed</span>;
      default:
        return null;
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      onClose();
    }
  };

  if (!tutor) return null;

  // Inline mode: render the same content as a panel embedded in a page
  // (no fixed overlay, no backdrop click-to-close). Used by the Earnings
  // Trends dashboard so switching months only updates this panel in place.
  const content = (
    <>
      <div className="modal-header">
        <div className="header-content">
          <h2>{tutor.name}</h2>
          <p className="modal-subtitle">
            {getMonthName(month)} {year} Summary
          </p>
        </div>
        {!inline && (
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        )}
      </div>

      <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading summary...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p className="error-message">{error}</p>
              <button className="retry-btn" onClick={fetchTutorSummary}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && summaryData && (
            <>
              {/* Tutor Information */}
              <div className="info-section">
                <div className="info-header">
                  <h3>Tutor Information</h3>
                  {getStatusBadge()}
                </div>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{tutor.email}</span>
                  </div>
                  {tutor.endorsedAt && (
                    <div className="info-item">
                      <span className="info-label">Endorsed At:</span>
                      <span className="info-value">{formatDate(tutor.endorsedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Endorsement Notes */}
              {summaryData.endorsementNotes && (
                <div className="notes-section">
                  <h3>Endorsement Notes</h3>
                  <div className="notes-content">
                    {summaryData.endorsementNotes}
                  </div>
                </div>
              )}

              {/* Lessons */}
              {summaryData.lessons && summaryData.lessons.length > 0 && (
                <div className="lessons-section">
                  <h3>Lessons ({summaryData.lessons.length})</h3>
                  <div className="lessons-table-wrapper">
                    <table className="lessons-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Tutee</th>
                          <th>Subject</th>
                          <th>Duration</th>
                          <th>Rate</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.lessons.map((lesson, index) => {
                          const hours = (lesson.duration || 0) / 60;
                          const rate = lesson.tutorRate || 0;
                          const amount = rate * hours;
                          
                          return (
                            <tr key={index}>
                              <td>{formatDate(lesson.date)}</td>
                              <td>{lesson.tutee?.name || 'N/A'}</td>
                              <td>{lesson.subject || 'N/A'}</td>
                              <td>{lesson.duration || 0} min</td>
                              <td>{formatCurrency(rate)}/hr</td>
                              <td className="amount-cell">{formatCurrency(amount)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payment Adjustments - Only show for non-profit sharers */}
              {!summaryData.profitShare && summaryData.adjustments && summaryData.adjustments.length > 0 && (
                <div className="adjustments-section">
                  <h3>Payment Adjustments</h3>
                  <div className="table-container">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Reason</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.adjustments.map((adjustment, index) => (
                          <tr key={index}>
                            <td>{adjustment.type}</td>
                            <td>{adjustment.reason}</td>
                            <td className={adjustment.amount >= 0 ? 'positive' : 'negative'}>
                              {adjustment.amount >= 0 ? '+' : ''}
                              {formatCurrency(adjustment.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recurring Payments - Only show for non-profit sharers */}
              {!summaryData.profitShare && summaryData.recurringPayments && summaryData.recurringPayments.length > 0 && (
                <div className="recurring-section">
                  <h3>Recurring Payments</h3>
                  <div className="table-container">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Reason</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.recurringPayments.map((payment, index) => (
                          <tr key={index}>
                            <td>{payment.type}</td>
                            <td>{payment.reason}</td>
                            <td className="positive">
                              +{formatCurrency(payment.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Profit Share Breakdown */}
              {summaryData.profitShare && (
                <div className="profit-share-section">
                  <h3>Commission Pool Share</h3>
                  <div className="profit-share-summary">
                    <div className="profit-share-row">
                      <span className="label">Your Allocation:</span>
                      <span className="value">{summaryData.profitShare.allocatedPercentage}%</span>
                    </div>
                    <div className="profit-share-row">
                      <span className="label">Total Commission Pool:</span>
                      <span className="value">{formatCurrency(summaryData.profitShare.totalCommissionPool)}</span>
                    </div>
                    <div className="profit-share-row">
                      <span className="label">Gross Profit Share:</span>
                      <span className="value">{formatCurrency(summaryData.profitShare.grossProfitShare)}</span>
                    </div>
                  </div>

                  {/* Deductions from Profit Share */}
                  {summaryData.profitShare.deductions && (
                    summaryData.profitShare.deductions.recurringPayments.length > 0 || 
                    summaryData.profitShare.deductions.adjustments.length > 0
                  ) && (
                    <div className="profit-share-deductions">
                      <h4>Deductions</h4>
                      
                      {/* Recurring Payment Deductions */}
                      {summaryData.profitShare.deductions.recurringPayments.length > 0 && (
                        <div className="deduction-group">
                          <h5>Recurring Payments</h5>
                          <table className="detail-table">
                            <thead>
                              <tr>
                                <th>Recipient</th>
                                <th>Reason</th>
                                <th>Full Amount</th>
                                <th>Your Share</th>
                              </tr>
                            </thead>
                            <tbody>
                              {summaryData.profitShare.deductions.recurringPayments.map((rp, index) => (
                                <tr key={index}>
                                  <td>{rp.tutor?.name || 'N/A'}</td>
                                  <td>{rp.reason}</td>
                                  <td>{formatCurrency(rp.amount)}</td>
                                  <td className={rp.viewerAmount >= 0 ? 'positive' : 'negative'}>
                                    {rp.viewerAmount >= 0 ? '+' : ''}
                                    {formatCurrency(rp.viewerAmount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Adjustment Deductions */}
                      {summaryData.profitShare.deductions.adjustments.length > 0 && (
                        <div className="deduction-group">
                          <h5>Adjustments</h5>
                          <table className="detail-table">
                            <thead>
                              <tr>
                                <th>Recipient</th>
                                <th>Type</th>
                                <th>Reason</th>
                                <th>Full Amount</th>
                                <th>Your Share</th>
                              </tr>
                            </thead>
                            <tbody>
                              {summaryData.profitShare.deductions.adjustments.map((adj, index) => (
                                <tr key={index}>
                                  <td>{adj.tutor?.name || 'N/A'}</td>
                                  <td>{adj.type}</td>
                                  <td>{adj.reason}</td>
                                  <td className={adj.amount >= 0 ? 'positive' : 'negative'}>
                                    {adj.amount >= 0 ? '+' : ''}
                                    {formatCurrency(adj.amount)}
                                  </td>
                                  <td className={adj.viewerAmount >= 0 ? 'positive' : 'negative'}>
                                    {adj.viewerAmount >= 0 ? '+' : ''}
                                    {formatCurrency(adj.viewerAmount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="profit-share-net">
                    <span className="label">Net Profit Share:</span>
                    <span className={`value ${summaryData.profitShare.netProfitShare < 0 ? 'negative' : ''}`}>
                      {formatCurrency(summaryData.profitShare.netProfitShare)}
                    </span>
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="totals-section">
                <div className="totals-row">
                  <span className="totals-label">Lessons Total:</span>
                  <span className="totals-value">
                    {formatCurrency(summaryData.totals?.lessonEarnings || summaryData.lessonEarnings || 0)}
                  </span>
                </div>
                {/* Only show adjustments and recurring for non-profit sharers */}
                {!summaryData.profitShare && (summaryData.totals?.adjustmentTotal !== 0 || summaryData.adjustmentTotal !== 0) && (
                  <div className="totals-row">
                    <span className="totals-label">Adjustments:</span>
                    <span className={`totals-value ${(summaryData.totals?.adjustmentTotal || summaryData.adjustmentTotal || 0) >= 0 ? 'positive' : 'negative'}`}>
                      {(summaryData.totals?.adjustmentTotal || summaryData.adjustmentTotal || 0) >= 0 ? '+' : ''}
                      {formatCurrency(summaryData.totals?.adjustmentTotal || summaryData.adjustmentTotal || 0)}
                    </span>
                  </div>
                )}
                {!summaryData.profitShare && (summaryData.totals?.recurringTotal !== 0 || summaryData.recurringTotal !== 0) && (
                  <div className="totals-row">
                    <span className="totals-label">Recurring Payments:</span>
                    <span className="totals-value positive">
                      +{formatCurrency(summaryData.totals?.recurringTotal || summaryData.recurringTotal || 0)}
                    </span>
                  </div>
                )}
                {/* Show profit share for profit sharers */}
                {summaryData.profitShare && (
                  <div className="totals-row">
                    <span className="totals-label">Commission Earnings (Profit Share):</span>
                    <span className={`totals-value ${summaryData.profitShare.netProfitShare >= 0 ? 'positive' : 'negative'}`}>
                      {summaryData.profitShare.netProfitShare >= 0 ? '+' : ''}
                      {formatCurrency(summaryData.profitShare.netProfitShare)}
                    </span>
                  </div>
                )}
                <div className="totals-row total">
                  <span className="totals-label">Total Payment:</span>
                  <span className="totals-value">
                    {formatCurrency(summaryData.totals?.finalAmount || summaryData.finalAmount || 0)}
                  </span>
                </div>
              </div>
            </>
          )}
      </div>

      {!inline && (
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {tutor.endorsementStatus === 'endorsed' && !tutor.invoiceGenerated && (
            <button 
              className="btn-primary" 
              onClick={() => onGenerateInvoice(tutor)}
              disabled={loading}
            >
              Generate Invoice
            </button>
          )}
        </div>
      )}
    </>
  );

  if (inline) {
    return <div className="tutor-detail-modal tutor-detail-inline">{content}</div>;
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="tutor-detail-modal">{content}</div>
    </div>
  );
};

export default TutorDetailModal;
